import { Routes } from '@angular/router';
import {PropertyListComponent} from './property-list/property-list.component';
import {CompanyComponent} from './company/company.component';
import {LoginComponent} from './login/login.component';
import {AuthGuard} from './auth.guard';
import {AuthCallbackComponent} from './auth-callback/auth-callback.component';
import {ContactListComponent} from './contact-list/contact-list.component';
import {LandingPageComponent} from './landing-page/landing-page.component';
import {AdminGuard} from './admin.guard';
import { AdminMobileGuard } from './admin-mobile.guard';
import {UserManagementComponent} from './user-management/user-management.component';
import {LogManagementComponent} from './log-management/log-management.component';
import {ResetPasswordComponent} from './reset-password/reset-password.component';
import {DashboardComponent} from './dashboard/dashboard.component';
import {KnowledgeBaseComponent} from './knowledge-base/knowledge-base.component';
import {AdminFeedbackComponent} from './admin-feedback/admin-feedback.component';
import {NotFoundComponent} from './not-found/not-found.component';
import {AnnouncementsComponent} from './announcements/announcements.component';
import {QrPosterTemplateComponent} from './qr-poster-template/qr-poster-template.component';
import {AppModeGuard} from './services/app-mode.guard';
import {RatingOverviewComponent} from './rating-overview/rating-overview.component';
import {HospitalityAiInsightsComponent} from './hospitality-ai-insights/hospitality-ai-insights.component';
import {MobileDesktopComponent} from './mobile-desktop/mobile-desktop.component';
import {AuthLinkErrorComponent} from './auth-link-error/auth-link-error.component';
import {MobileGuard} from './mobile.guard';
import { AccountSettingsShellComponent } from './account-settings/account-settings-shell.component';
import { AccountSettingsAccountComponent } from './account-settings/account-settings-account.component';
import { AccountSettingsSecurityComponent } from './account-settings/account-settings-security.component';
import { AccountSettingsDangerComponent } from './account-settings/account-settings-danger.component';
import { AccountSettingsTeamAccessComponent } from './account-settings/account-settings-team-access.component';
import { AccountSettingsBillingComponent } from './account-settings/account-settings-billing.component';
import { AccountSettingsBrandedDomainComponent } from './account-settings/account-settings-branded-domain.component';
import { SeatsRbacAdminComponent } from './seats-rbac-admin/seats-rbac-admin.component';
import { AcceptInviteComponent } from './accept-invite/accept-invite.component';
import { PermissionGuard } from './permission.guard';
import {IntegrationsOverviewComponent} from './integrations/integrations-overview.component';
import {IntegrationSetupWizardComponent} from './integrations/integration-setup-wizard.component';
import {SftpSetupWizardComponent} from './integrations/sftp-setup-wizard.component';
import {IntegrationStatusComponent} from './integrations/integration-status.component';
import { AdminIntegrationsComponent } from './admin-integrations/admin-integrations.component';
import { AdminEmailCampaignsComponent } from './admin-email-campaigns/admin-email-campaigns.component';
import { AdminEmailCampaignEditorComponent } from './admin-email-campaigns/admin-email-campaign-editor.component';
import { AdminSupportCenterComponent } from './admin-support-center/admin-support-center.component';
import { AdminDashboardComponent } from './admin-dashboard/admin-dashboard.component';
import { AuditLogComponent } from './audit-log/audit-log.component';
import { AdminNotificationsComponent } from './admin-notifications/admin-notifications.component';
import { AdminNotificationEditorComponent } from './admin-notifications/admin-notification-editor.component';
import { AdminNotificationDetailComponent } from './admin-notifications/admin-notification-detail.component';
import { AdminPlatformAnnouncementsComponent } from './admin-platform-announcements/admin-platform-announcements.component';
import { AdminPlatformAnnouncementEditorComponent } from './admin-platform-announcements/admin-platform-announcement-editor.component';
import { AdminSignOrderSettingsComponent } from './admin/settings/sign-order-settings.component';
import { AdminSignOrderDetailComponent } from './admin/sign-orders/sign-order-detail.component';
import { AdminSignOrderListComponent } from './admin/sign-orders/sign-order-list.component';
import { AdminAccountSecurityComponent } from './admin/account-security/admin-account-security.component';

export const routes: Routes = [
  { path: '', redirectTo: 'login', pathMatch: 'full' }, // Add new route
  { path: 'login', component: LoginComponent }, // Login page route
  { path: 'register', component: LoginComponent, data: { initialAuthMode: 'register' } },
  { path: 'auth-callback', component: AuthCallbackComponent }, // Add the auth-callback route
  { path: 'accept-invite', component: AcceptInviteComponent },
  { path: 'auth-link-error', component: AuthLinkErrorComponent },
  { path: 'reset-password/:token', component: ResetPasswordComponent },
  { path: 'mobile-desktop', component: MobileDesktopComponent, canActivate: [AuthGuard] },
  { path: 'dashboard', component: DashboardComponent, canActivate: [AuthGuard, MobileGuard, PermissionGuard], data: { permission: 'company.dashboard.read' } },
  { path: 'properties', component: PropertyListComponent, canActivate: [AuthGuard, MobileGuard, PermissionGuard], data: { permission: 'company.properties.read' } }, // Property listing, protected
  { path: 'company', component: CompanyComponent, canActivate: [AuthGuard, MobileGuard, PermissionGuard], data: { permission: 'company.company_profile.read' } }, // Company page, protected
  { path: 'qr-poster-template', component: QrPosterTemplateComponent, canActivate: [AuthGuard, MobileGuard, PermissionGuard], data: { permission: 'company.qr_poster_texts.read' } },
  {
    path: 'contacts',
    component: ContactListComponent,
    canActivate: [AuthGuard, AppModeGuard, MobileGuard, PermissionGuard],
    data: { appModes: ['emergency'], redirectTo: '/dashboard', permission: 'company.contacts.read' }
  },
  {
    path: 'rating-overview',
    component: RatingOverviewComponent,
    canActivate: [AuthGuard, AppModeGuard, MobileGuard, PermissionGuard],
    data: { appModes: ['hospitality'], redirectTo: '/dashboard', permission: 'company.ratings.read' }
  },
  {
    path: 'hospitality-ai',
    component: HospitalityAiInsightsComponent,
    canActivate: [AuthGuard, AppModeGuard, PermissionGuard],
    data: { appModes: ['hospitality'], redirectTo: '/dashboard', allowAdmin: true, permission: 'company.hospitality_ai.read' }
  },
  { path: 'announcements', component: AnnouncementsComponent, canActivate: [AuthGuard, MobileGuard, PermissionGuard], data: { permission: 'company.announcements.read' } },
  { path: 'audit-logs', component: AuditLogComponent, canActivate: [AuthGuard, MobileGuard, PermissionGuard], data: { permission: 'company.audit_logs.read' } },
  {
    path: 'account/settings',
    component: AccountSettingsShellComponent,
    canActivate: [AuthGuard, MobileGuard],
    children: [
      { path: '', redirectTo: 'account', pathMatch: 'full' },
      { path: 'account', component: AccountSettingsAccountComponent },
      {
        path: 'branded-domain',
        component: AccountSettingsBrandedDomainComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'company.company_profile.read' }
      },
      {
        path: 'billing',
        component: AccountSettingsBillingComponent,
        canActivate: [PermissionGuard],
        data: { permission: 'company.billing.read' }
      },
      { path: 'security', component: AccountSettingsSecurityComponent },
      {
        path: 'team-access',
        component: AccountSettingsTeamAccessComponent,
        canActivate: [PermissionGuard],
        data: { anyPermissions: ['company.users.read', 'company.users.invite', 'company.users.update', 'company.users.remove'] }
      },
      { path: 'danger', component: AccountSettingsDangerComponent },
    ]
  },
  { path: 'account/delete', redirectTo: 'account/settings/danger', pathMatch: 'full' },
  { path: 'landing-page/:companyId/:propertyId', component: LandingPageComponent },
  { path: 'admin/dashboard', component: AdminDashboardComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/user-management', component: UserManagementComponent, canActivate: [AdminGuard, AdminMobileGuard] }, // Admin-only route
  { path: 'admin/account/security', component: AdminAccountSecurityComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/log-management', component: LogManagementComponent, canActivate: [AdminGuard, AdminMobileGuard] }, // Admin-only route
  { path: 'admin/knowledge-base', component: KnowledgeBaseComponent, canActivate: [AdminGuard, AdminMobileGuard] }, // Admin-only route
  { path: 'admin/feedback', component: AdminFeedbackComponent, canActivate: [AdminGuard, AdminMobileGuard] }, // Admin-only route
  { path: 'admin/seats-rbac', component: SeatsRbacAdminComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/integrations', component: AdminIntegrationsComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/email-campaigns', component: AdminEmailCampaignsComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/email-campaigns/new', component: AdminEmailCampaignEditorComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/email-campaigns/:id', component: AdminEmailCampaignEditorComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/notifications', component: AdminNotificationsComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/notifications/new', component: AdminNotificationEditorComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/notifications/:id', component: AdminNotificationDetailComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/platform-announcements', component: AdminPlatformAnnouncementsComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/platform-announcements/new', component: AdminPlatformAnnouncementEditorComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/platform-announcements/:id', component: AdminPlatformAnnouncementEditorComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'admin/support-center', component: AdminSupportCenterComponent, canActivate: [AdminGuard, AdminMobileGuard] },
  { path: 'company/integrations', component: IntegrationsOverviewComponent, canActivate: [AuthGuard] },
  { path: 'company/integrations/sftp/setup', component: SftpSetupWizardComponent, canActivate: [AuthGuard] },
  { path: 'company/integrations/:provider/setup', component: IntegrationSetupWizardComponent, canActivate: [AuthGuard] },
  { path: 'company/integrations/:id/status', component: IntegrationStatusComponent, canActivate: [AuthGuard] },
  {
    path: 'admin/sign-orders',
    component: AdminSignOrderListComponent,
    canActivate: [AuthGuard, AdminMobileGuard, PermissionGuard],
    data: { permission: 'platform.sign_orders.read' },
  },
  {
    path: 'admin/sign-orders/settings',
    component: AdminSignOrderSettingsComponent,
    canActivate: [AuthGuard, AdminMobileGuard, PermissionGuard],
    data: { permission: 'platform.sign_orders.settings' },
  },
  {
    path: 'admin/sign-orders/:publicId',
    component: AdminSignOrderDetailComponent,
    canActivate: [AuthGuard, AdminMobileGuard, PermissionGuard],
    data: { permission: 'platform.sign_orders.read' },
  },
  { path: '**', component: NotFoundComponent },
];
